/**
 * Data layer tracking, pure logic.
 *
 * The contract is `docs/specs/data-layer-specification.md`; the behaviour is
 * specified by the `chat-tracking` capability. Everything that can be decided
 * without a DOM lives here (allow-list, normalisation, ids, session record,
 * push builder, tracker), so the host script only wires events to it and Jest
 * can check every shape.
 *
 * Privacy rule of this module: nothing the visitor typed ever reaches a push.
 * The only free text is `dhsvchat_button_text` (the rendered label of a control)
 * and every URL is reduced to origin and path.
 */

export const EVENT_PREFIX = 'dhsvchat_';
export const SESSION_STORAGE_KEY = 'dhsvchat-session';
/** `dhsvchat_conversation_id` while the host has no valid id. */
export const NO_CONVERSATION_ID = 'none';

export const MAX_TEXT_LENGTH = 100;
export const MAX_URL_LENGTH = 100;
export const MIN_MESSAGE_INDEX = 1;
export const MAX_MESSAGE_INDEX = 100000;
export const MIN_RESPONSE_TIME_MS = 0;
export const MAX_RESPONSE_TIME_MS = 3600000;
export const MIN_POSITION = 1;
export const MAX_POSITION = 50;
export const MAX_PARAMS = 12;
/** Identical `button_click` events inside this window count once. */
export const BUTTON_DEDUPE_MS = 500;

const CONVERSATION_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
const SESSION_ID_PATTERN = /^sess_[0-9a-f]{12}$/;
const BUTTON_VALUE_PATTERN = /^[a-z0-9_]{1,40}$/;
const TOPIC_PATTERN = /^[a-z0-9_-]{1,40}$/;
const SOURCE_PATTERN = /^[a-z][a-z0-9_-]{0,31}$/;
// The same rule as `SECTION_ID_PATTERN` in `proactive-sections.ts`.
const SECTION_ID_PATTERN = /^[A-Za-z0-9_.-]{1,64}$/;

export type DeviceType = 'mobile' | 'tablet' | 'desktop';
export type Primitive = string | number | boolean;
export type PushParams = Record< string, Primitive >;

export const BUTTON_TYPES = [ 'ui', 'suggestion', 'cta' ] as const;
export const BUTTON_LOCATIONS = [
	'header',
	'footer',
	'welcome_screen',
	'bot_message',
	'history_view',
] as const;
export const FALLBACK_REASONS = [ 'network', 'timeout', 'empty' ] as const;
export const RATINGS = [ 'thumbs_up', 'thumbs_down' ] as const;
export const PROACTIVE_CHOICES = [ 'question', 'pill' ] as const;
export const MAX_PILL_INDEX = 2;
/** Schnellfragen of the Chat-Eingabe: at most six, so indexes 0-5. */
export const MAX_QUICK_INDEX = 5;
const DEVICE_TYPES: readonly DeviceType[] = [ 'mobile', 'tablet', 'desktop' ];
const VARIANTS = [ 'default', 'sidebar', 'fullscreen', 'island' ] as const;

// ---------------------------------------------------------------------------
// Normalisation
// ---------------------------------------------------------------------------

// Emoji, pictographs, skin tones, regional indicators, the emoji variation
// selector, the zero-width joiner and the keycap combiner.
const EMOJI =
	/[\p{Extended_Pictographic}\p{Emoji_Modifier}\u{1F1E6}-\u{1F1FF}️‍⃣]/gu;

function cleanText( raw: string ): string {
	return Array.from( raw.replace( EMOJI, '' ).replace( /\s+/g, ' ' ).trim() )
		.slice( 0, MAX_TEXT_LENGTH )
		.join( '' )
		.trim();
}

/**
 * The text of a control as GA4 should see it: rendered text without emoji,
 * whitespace collapsed, trimmed, at most 100 characters. Icon-only controls
 * fall back to their `aria-label`; `undefined` when nothing is left.
 *
 * @param {unknown} raw       The rendered text.
 * @param {unknown} ariaLabel The control's accessible name.
 */
export function normalizeButtonText(
	raw: unknown,
	ariaLabel?: unknown
): string | undefined {
	const text = typeof raw === 'string' ? cleanText( raw ) : '';
	if ( text !== '' ) {
		return text;
	}
	const label = typeof ariaLabel === 'string' ? cleanText( ariaLabel ) : '';
	return label === '' ? undefined : label;
}

/**
 * A link target reduced to `origin + pathname`: http(s) only, no query,
 * fragment or credentials. Dropped when it cannot be parsed, is longer than
 * 100 characters or its path looks personal (`@`, `%40`, six digits in a row).
 *
 * @param {unknown} href The link, absolute or relative.
 * @param {string}  base The document URL relative links resolve against.
 */
export function sanitizeButtonUrl(
	href: unknown,
	base: string
): string | undefined {
	if ( typeof href !== 'string' || href.trim() === '' ) {
		return undefined;
	}
	let url: URL;
	try {
		url = new URL( href, base );
	} catch {
		return undefined;
	}
	if ( url.protocol !== 'http:' && url.protocol !== 'https:' ) {
		return undefined;
	}
	const reduced = url.origin + url.pathname;
	if (
		reduced.length > MAX_URL_LENGTH ||
		/@|%40|\d{6,}/i.test( url.pathname )
	) {
		return undefined;
	}
	return reduced;
}

/**
 * Integrator-supplied topic: lowercase slug or nothing.
 *
 * @param {unknown} raw The topic of the open.
 */
export function sanitizeTopic( raw: unknown ): string | undefined {
	if ( typeof raw !== 'string' ) {
		return undefined;
	}
	const topic = raw.trim().toLowerCase();
	return TOPIC_PATTERN.test( topic ) ? topic : undefined;
}

// ---------------------------------------------------------------------------
// Ids and device
// ---------------------------------------------------------------------------

/**
 * The specification's `resolveDeviceType()`, tablet first, plus iPadOS 13+
 * (which reports a Mac): a Mac with more than one touch point is a tablet.
 *
 * @param {string} ua             `navigator.userAgent`.
 * @param {number} maxTouchPoints `navigator.maxTouchPoints`.
 */
export function resolveDeviceType(
	ua: string,
	maxTouchPoints: number = 0
): DeviceType {
	if ( /iPad|Tablet|PlayBook|Silk|(Android(?!.*Mobi))/i.test( ua ) ) {
		return 'tablet';
	}
	if ( /Macintosh/.test( ua ) && maxTouchPoints > 1 ) {
		return 'tablet';
	}
	if ( /Mobi|Android|iPhone|iPod/i.test( ua ) ) {
		return 'mobile';
	}
	return 'desktop';
}

/**
 * A version 4 UUID: `crypto.randomUUID()` where it exists, else built from
 * `crypto.getRandomValues()` (`randomUUID` is missing on insecure `http:`
 * pages). Throws only when there is no crypto at all.
 */
export function generateUuid(): string {
	const cryptoApi: Crypto | undefined =
		typeof crypto === 'undefined' ? undefined : crypto;
	if ( cryptoApi && typeof cryptoApi.randomUUID === 'function' ) {
		return cryptoApi.randomUUID();
	}
	if ( ! cryptoApi || typeof cryptoApi.getRandomValues !== 'function' ) {
		throw new Error( 'No crypto available' );
	}
	const bytes = cryptoApi.getRandomValues( new Uint8Array( 16 ) );
	// Version 4 (high nibble 4) and the RFC 4122 variant (bits 10), without bit operators.
	bytes[ 6 ] = 0x40 + ( ( bytes[ 6 ] ?? 0 ) % 16 );
	bytes[ 8 ] = 0x80 + ( ( bytes[ 8 ] ?? 0 ) % 64 );
	const hex = Array.from( bytes, ( byte ) =>
		byte.toString( 16 ).padStart( 2, '0' )
	).join( '' );
	return `${ hex.slice( 0, 8 ) }-${ hex.slice( 8, 12 ) }-${ hex.slice(
		12,
		16
	) }-${ hex.slice( 16, 20 ) }-${ hex.slice( 20 ) }`;
}

/** `sess_` and 12 lowercase hex characters; `Math.random()` is the last resort. */
export function createSessionId(): string {
	let hex: string;
	try {
		hex = generateUuid().replace( /-/g, '' );
	} catch {
		hex = '';
		while ( hex.length < 12 ) {
			hex += Math.floor( Math.random() * 16 ).toString( 16 );
		}
	}
	return `sess_${ hex.slice( 0, 12 ).toLowerCase() }`;
}

// ---------------------------------------------------------------------------
// Session record
// ---------------------------------------------------------------------------

export type SessionRecord = {
	id: string;
	device: DeviceType;
	started: boolean;
};

export type SessionHandle = SessionRecord & { markStarted: () => void };

type SessionOptions = {
	storageAllowed: boolean;
	ua: string;
	maxTouchPoints: number;
	win?: Pick< Window, 'sessionStorage' >;
};

function parseSessionRecord( raw: string | null ): SessionRecord | null {
	if ( raw === null ) {
		return null;
	}
	try {
		const data: unknown = JSON.parse( raw );
		if ( ! isPlainObject( data ) ) {
			return null;
		}
		const { id, device, started } = data as Record< string, unknown >;
		if (
			typeof id !== 'string' ||
			! SESSION_ID_PATTERN.test( id ) ||
			! DEVICE_TYPES.includes( device as DeviceType )
		) {
			return null;
		}
		return {
			id,
			device: device as DeviceType,
			started: started === true,
		};
	} catch {
		return null;
	}
}

/**
 * The session of this browser tab: read from `sessionStorage` when storage is
 * allowed (one JSON record), otherwise kept in memory for the page view. Any
 * storage error switches to memory for good; nothing is retried. Call it at the
 * first push, never earlier: that is when the key is first written.
 *
 * @param {SessionOptions} options Storage permission and the device inputs.
 */
export function loadSession( options: SessionOptions ): SessionHandle {
	let storageOk = options.storageAllowed;
	const win = options.win ?? window;

	const persist = ( record: SessionRecord ) => {
		if ( ! storageOk ) {
			return;
		}
		try {
			win.sessionStorage.setItem(
				SESSION_STORAGE_KEY,
				JSON.stringify( {
					id: record.id,
					device: record.device,
					started: record.started,
				} )
			);
		} catch {
			storageOk = false;
		}
	};

	let record: SessionRecord | null = null;
	if ( storageOk ) {
		try {
			record = parseSessionRecord(
				win.sessionStorage.getItem( SESSION_STORAGE_KEY )
			);
		} catch {
			storageOk = false;
		}
	}
	if ( ! record ) {
		record = {
			id: createSessionId(),
			device: resolveDeviceType( options.ua, options.maxTouchPoints ),
			started: false,
		};
		persist( record );
	}

	const handle: SessionHandle = {
		...record,
		markStarted() {
			handle.started = true;
			persist( handle );
		},
	};
	return handle;
}

// ---------------------------------------------------------------------------
// Allow-list
// ---------------------------------------------------------------------------

type Validator = ( value: unknown, base: string ) => Primitive | undefined;
type ParamSpec = { required: boolean; validate: Validator };

const integerIn =
	( min: number, max: number ): Validator =>
	( value ) =>
		typeof value === 'number' &&
		Number.isInteger( value ) &&
		value >= min &&
		value <= max
			? value
			: undefined;

const oneOf =
	( allowed: readonly string[] ): Validator =>
	( value ) =>
		typeof value === 'string' && allowed.includes( value )
			? value
			: undefined;

const isBoolean: Validator = ( value ) =>
	typeof value === 'boolean' ? value : undefined;

const messageIndex = integerIn( MIN_MESSAGE_INDEX, MAX_MESSAGE_INDEX );

const sectionId: ParamSpec = {
	required: true,
	validate: ( value ) =>
		typeof value === 'string' && SECTION_ID_PATTERN.test( value )
			? value
			: undefined,
};

export type TrackEventName =
	| 'message_sent'
	| 'bot_response'
	| 'fallback'
	| 'rating'
	| 'button_click'
	| 'proactive_shown'
	| 'proactive_accepted'
	| 'proactive_dismissed'
	| 'quick_question_clicked';

export type ProactiveTrackEvent =
	| 'proactive_shown'
	| 'proactive_accepted'
	| 'proactive_dismissed';

/** Host-side events validated against the allow-list (`onProactive`). */
export type HostTrackEvent = ProactiveTrackEvent | 'quick_question_clicked';

/**
 * The only place that lists the keys an iframe-originated event may carry, in
 * the order they appear in the push. A new key needs a spec change.
 */
export const TRACK_EVENTS: Readonly<
	Record< TrackEventName, Readonly< Record< string, ParamSpec > > >
> = {
	message_sent: {
		dhsvchat_message_index: { required: true, validate: messageIndex },
		dhsvchat_is_first_message: { required: true, validate: isBoolean },
	},
	bot_response: {
		dhsvchat_message_index: { required: true, validate: messageIndex },
		dhsvchat_response_time_ms: {
			required: true,
			validate: integerIn( MIN_RESPONSE_TIME_MS, MAX_RESPONSE_TIME_MS ),
		},
	},
	fallback: {
		dhsvchat_message_index: { required: true, validate: messageIndex },
		dhsvchat_fallback_reason: {
			required: true,
			validate: oneOf( FALLBACK_REASONS ),
		},
	},
	rating: {
		dhsvchat_message_index: { required: true, validate: messageIndex },
		dhsvchat_rating: { required: true, validate: oneOf( RATINGS ) },
	},
	button_click: {
		dhsvchat_message_index: { required: false, validate: messageIndex },
		dhsvchat_button_text: {
			required: false,
			validate: ( value ) => normalizeButtonText( value ),
		},
		dhsvchat_button_type: {
			required: true,
			validate: oneOf( BUTTON_TYPES ),
		},
		dhsvchat_button_location: {
			required: true,
			validate: oneOf( BUTTON_LOCATIONS ),
		},
		dhsvchat_button_value: {
			required: true,
			validate: ( value ) =>
				typeof value === 'string' && BUTTON_VALUE_PATTERN.test( value )
					? value
					: undefined,
		},
		dhsvchat_button_url: {
			required: false,
			validate: ( value, base ) => sanitizeButtonUrl( value, base ),
		},
		dhsvchat_button_position: {
			required: false,
			validate: integerIn( MIN_POSITION, MAX_POSITION ),
		},
	},
	// Host-side proactive layer: ids and indexes only, never the texts.
	proactive_shown: {
		dhsvchat_section_id: sectionId,
	},
	proactive_accepted: {
		dhsvchat_section_id: sectionId,
		dhsvchat_choice: {
			required: true,
			validate: oneOf( PROACTIVE_CHOICES ),
		},
		dhsvchat_pill_index: {
			required: false,
			validate: integerIn( 0, MAX_PILL_INDEX ),
		},
	},
	proactive_dismissed: {
		dhsvchat_section_id: sectionId,
	},
	// A Schnellfrage under the Chat-Eingabe: its index only, never the text.
	quick_question_clicked: {
		dhsvchat_quick_index: {
			required: true,
			validate: integerIn( 0, MAX_QUICK_INDEX ),
		},
	},
};

/**
 * A plain object (also from another realm): prototype is `null` or the
 * prototype of an object literal.
 *
 * @param {unknown} value Anything.
 */
export function isPlainObject( value: unknown ): boolean {
	if ( typeof value !== 'object' || value === null ) {
		return false;
	}
	const proto = Object.getPrototypeOf( value );
	return proto === null || Object.getPrototypeOf( proto ) === null;
}

function documentUrl(): string {
	return typeof window !== 'undefined' && window.location
		? window.location.href
		: 'http://localhost/';
}

/**
 * Validate a `track` message from the iframe against the allow-list. Unknown
 * events, a non-plain message or params, and more than 12 keys drop the whole
 * message; unknown keys and invalid optional keys are dropped; an invalid or
 * missing required key drops the event. Text and URLs are normalised again.
 *
 * @param {unknown} data The `track` message.
 * @param {string}  base The document URL, for relative links.
 */
export function validateTrackMessage(
	data: unknown,
	base: string = documentUrl()
): { name: TrackEventName; params: PushParams } | null {
	if ( ! isPlainObject( data ) ) {
		return null;
	}
	const { event, params } = data as Record< string, unknown >;
	if (
		typeof event !== 'string' ||
		! Object.prototype.hasOwnProperty.call( TRACK_EVENTS, event )
	) {
		return null;
	}
	if (
		! isPlainObject( params ) ||
		Object.keys( params as object ).length > MAX_PARAMS
	) {
		return null;
	}
	const name = event as TrackEventName;
	const received = params as Record< string, unknown >;
	const result: PushParams = {};
	for ( const [ key, spec ] of Object.entries( TRACK_EVENTS[ name ] ) ) {
		const value = Object.prototype.hasOwnProperty.call( received, key )
			? spec.validate( received[ key ], base )
			: undefined;
		if ( value === undefined ) {
			if ( spec.required ) {
				return null;
			}
			continue;
		}
		result[ key ] = value;
	}
	return { name, params: result };
}

// ---------------------------------------------------------------------------
// Push builder
// ---------------------------------------------------------------------------

export type PushState = {
	sessionId: string;
	conversationId: string;
	deviceType: DeviceType;
};

export type DataLayerPush = {
	event: string;
	dhsvchat: Record< string, Primitive >;
};

/**
 * One event in the shape of the specification: the base fields first, then the
 * message index when the event has one, then the event's own fields. Keys that
 * are not prefixed `dhsvchat_` never get in.
 *
 * @param {PushState}  state  Session, conversation and device.
 * @param {string}     name   Event name without the prefix.
 * @param {PushParams} params The event's fields.
 * @param {number}     now    `Date.now()`.
 */
export function buildPush(
	state: PushState,
	name: string,
	params: PushParams,
	now: number
): DataLayerPush {
	const { dhsvchat_message_index: index, ...rest } = params;
	const fields: Record< string, Primitive > = {
		dhsvchat_session_id: state.sessionId,
		dhsvchat_conversation_id: state.conversationId,
		dhsvchat_device_type: state.deviceType,
		dhsvchat_timestamp: now,
	};
	if ( index !== undefined ) {
		fields.dhsvchat_message_index = index;
	}
	for ( const [ key, value ] of Object.entries( rest ) ) {
		if ( key.startsWith( EVENT_PREFIX ) && ! ( key in fields ) ) {
			fields[ key ] = value;
		}
	}
	return { event: `${ EVENT_PREFIX }${ name }`, dhsvchat: fields };
}

// ---------------------------------------------------------------------------
// Tracker
// ---------------------------------------------------------------------------

export type TrackerOptions = {
	/** `data-tracking === '1'`. */
	enabled: boolean;
	/** `data-tracking-storage === '1'`. */
	storageAllowed: boolean;
	win?: TrackerWindow;
};

export type TrackerWindow = Partial< Window > & {
	dataLayer?: unknown;
	dhsvChatTracking?: unknown;
};

export type OpenInfo = {
	source: string;
	variant: string;
	topic?: string;
	sessionStart: boolean;
};

export type Tracker = {
	onOpen: ( payload: OpenInfo ) => void;
	onClose: () => void;
	onTrack: ( data: unknown ) => void;
	onConversationChanged: ( id: unknown ) => void;
	onTeaserClick: (
		value: 'teaser_open' | 'teaser_close',
		text: string
	) => void;
	/**
	 * Host-side events: the proactive layer of the teaser and the
	 * Schnellfragen of the Chat-Eingabe. The params go through the same
	 * allow-list as an iframe `track` message.
	 */
	onProactive: ( name: HostTrackEvent, params: PushParams ) => void;
	onPageRestored: () => void;
};

const TRACKER_KEY = Symbol.for( 'dhsv100-ai-chat.tracker' );

/**
 * The page's tracker. A singleton per window: a second call returns the first
 * one, so a second copy of the host script cannot push everything twice.
 *
 * @param {TrackerOptions} options Switches from the host container.
 */
export function createTracker( options: TrackerOptions ): Tracker {
	const win = ( options.win ?? window ) as TrackerWindow &
		Record< symbol, unknown >;
	const existing = win[ TRACKER_KEY ];
	if ( existing ) {
		return existing as Tracker;
	}

	const { enabled } = options;
	let session: SessionHandle | null = null;
	let conversationId = NO_CONVERSATION_ID;
	let openRecorded = false;
	let openedAt = 0;
	let messageCount = 0;
	const lastButton = new Map< string, number >();
	const handled = new WeakSet< object >();

	const getSession = (): SessionHandle => {
		if ( ! session ) {
			session = loadSession( {
				storageAllowed: options.storageAllowed,
				ua: win.navigator?.userAgent ?? '',
				maxTouchPoints: win.navigator?.maxTouchPoints ?? 0,
				win: win as Pick< Window, 'sessionStorage' >,
			} );
		}
		return session;
	};

	const send = ( entry: unknown ): void => {
		( win.dataLayer as { push: ( value: unknown ) => unknown } ).push(
			entry
		);
	};

	/**
	 * One logical event: the reset push, a possible `session_start` and the
	 * event, whole or not at all. Nothing here may throw into the chat.
	 * @param name
	 * @param params
	 * @param startsSession
	 */
	const pushUnit = (
		name: string,
		params: PushParams,
		startsSession = false
	): boolean => {
		if ( ! enabled || win.dhsvChatTracking === false ) {
			return false;
		}
		try {
			const now = Date.now();
			if ( name === 'button_click' ) {
				const key = `${ params.dhsvchat_button_value }|${ params.dhsvchat_button_location }`;
				const last = lastButton.get( key );
				if ( last !== undefined && now - last < BUTTON_DEDUPE_MS ) {
					return false;
				}
			}
			if ( ! win.dataLayer ) {
				win.dataLayer = [];
			}
			if (
				typeof ( win.dataLayer as { push?: unknown } ).push !==
				'function'
			) {
				return false;
			}
			const current = getSession();
			const state: PushState = {
				sessionId: current.id,
				conversationId,
				deviceType: current.device,
			};
			if ( startsSession && ! current.started ) {
				send( { dhsvchat: null } );
				send( buildPush( state, 'session_start', {}, now ) );
				current.markStarted();
			}
			send( { dhsvchat: null } );
			send( buildPush( state, name, params, now ) );
			if ( name === 'button_click' ) {
				lastButton.set(
					`${ params.dhsvchat_button_value }|${ params.dhsvchat_button_location }`,
					now
				);
			}
			if ( name === 'message_sent' || name === 'bot_response' ) {
				messageCount += 1;
			}
			return true;
		} catch {
			return false;
		}
	};

	const tracker: Tracker = {
		onOpen( payload ) {
			if ( ! payload.sessionStart || openRecorded ) {
				return;
			}
			const topic = sanitizeTopic( payload.topic );
			const params: PushParams = {
				dhsvchat_source: SOURCE_PATTERN.test( payload.source )
					? payload.source
					: 'api',
				dhsvchat_variant: ( VARIANTS as readonly string[] ).includes(
					payload.variant
				)
					? payload.variant
					: 'default',
			};
			if ( topic ) {
				params.dhsvchat_topic = topic;
			}
			if ( pushUnit( 'open', params ) ) {
				openRecorded = true;
				openedAt = Date.now();
				messageCount = 0;
			}
		},

		onClose() {
			if ( ! openRecorded ) {
				return;
			}
			openRecorded = false;
			pushUnit( 'close', {
				dhsvchat_duration_seconds: Math.round(
					( Date.now() - openedAt ) / 1000
				),
				dhsvchat_message_count: messageCount,
			} );
		},

		onTrack( data ) {
			if ( ! enabled ) {
				return;
			}
			if ( typeof data === 'object' && data !== null ) {
				if ( handled.has( data ) ) {
					return;
				}
				handled.add( data );
			}
			const valid = validateTrackMessage( data );
			if ( ! valid ) {
				return;
			}
			const startsSession =
				valid.name === 'message_sent' ||
				( valid.name === 'button_click' &&
					valid.params.dhsvchat_button_location !== 'teaser' &&
					valid.params.dhsvchat_button_value !== 'close' );
			pushUnit( valid.name, valid.params, startsSession );
		},

		onConversationChanged( id ) {
			if ( enabled && typeof id === 'string' ) {
				if ( CONVERSATION_ID_PATTERN.test( id ) ) {
					conversationId = id;
				}
			}
		},

		onTeaserClick( value, text ) {
			const label = normalizeButtonText( text );
			pushUnit( 'button_click', {
				...( label ? { dhsvchat_button_text: label } : {} ),
				dhsvchat_button_type: 'ui',
				dhsvchat_button_location: 'teaser',
				dhsvchat_button_value: value,
			} );
		},

		onProactive( name, params ) {
			if ( ! enabled ) {
				return;
			}
			const valid = validateTrackMessage( { event: name, params } );
			if ( valid ) {
				pushUnit( valid.name, valid.params );
			}
		},

		onPageRestored() {
			openRecorded = false;
			openedAt = 0;
			messageCount = 0;
		},
	};

	Object.defineProperty( win, TRACKER_KEY, {
		value: tracker,
		configurable: true,
		enumerable: false,
		writable: true,
	} );
	return tracker;
}

/**
 * Test seam: forget the page's tracker so the next `createTracker()` builds a new one.
 * @param win
 */
export function resetTrackerSingleton( win: object = window ): void {
	delete ( win as Record< symbol, unknown > )[ TRACKER_KEY ];
}
